/*
 * TSS PDP behaviours. Vanilla, no bundler. Everything lives inside custom
 * elements (or document-level delegation) so it re-initialises for free when
 * <product-looks> swaps the PDP via the Section Rendering API.
 */

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Shared-element name for the tile <-> zoom-modal morph. Only ever assigned to
// elements during an active transition (and only one modal opens at a time), so
// a single static name is safe across multiple galleries on a page.
const PGAL_ZOOM_NAME = 'pgal-zoom';

/* ---------------------------------------------------------------- Gallery -- */
if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.tiles = Array.from(this.querySelectorAll('.pgal__tile'));
        this.modal = this.querySelector('[data-modal]');
        this.stage = this.querySelector('[data-stage]');
        this.pane = this.querySelector('[data-pane]');
        this.lens = this.querySelector('[data-lens]');
        this.figures = Array.from(this.querySelectorAll('[data-figure]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        this.index = 0;
        this.zoomQuery = window.matchMedia('(min-width: 990px) and (pointer: fine)');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.tiles.forEach((tile) => tile.addEventListener('click', () => this.open(Number(tile.dataset.index) || 0)));
        this.querySelectorAll('[data-modal-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        // The full-size dialog covers the overlay, so backdrop clicks never reach
        // it. Instead, close on any click inside the modal that isn't real content
        // (image/zoom stage, nav arrows, close button, or the thumbs strip).
        this.modal?.addEventListener('click', (e) => {
          if (e.target.closest('.pgal__stage, .pgal__pane, .pgal__modal-btn, .pgal__thumbs')) return;
          this.close();
        });
        this.querySelector('[data-modal-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-modal-next]')?.addEventListener('click', () => this.step(1));
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(Number(t.dataset.thumb) || 0)));

        this.onKeydown = this.onKeydown.bind(this);
        this.stage?.addEventListener('pointermove', this.onZoom);
        this.stage?.addEventListener('pointerleave', this.centerZoom);
        // Stands in for draggable="false" — a native drag hijacks the zoom.
        this.stage?.addEventListener('dragstart', (e) => e.preventDefault());
        // Mobile swipe carousel + pagination is handled by <splide-slider>.
      }

      // .pgal__stage-img is the <picture>; zoom needs the <img> inside it.
      get activeImg() {
        return this.figures[this.index]?.querySelector('.pgal__stage-img img');
      }

      // Full-res image inside a grid tile (the last <img>, i.e. the sharp one
      // stacked over any cached placeholder).
      tileImg(index) {
        const imgs = this.tiles[index]?.querySelectorAll('.pgal__media img');
        return imgs && imgs[imgs.length - 1];
      }
      stageImg(index) {
        return this.figures[index]?.querySelector('.pgal__stage-img img');
      }

      // Wrap a DOM mutation in a shared-element view transition that morphs the
      // `from` image into the `to` image. Falls back to the plain mutation when
      // the API is unavailable or the user prefers reduced motion.
      morph(from, to, mutate) {
        if (!document.startViewTransition || this.reduceMotion.matches) return void mutate();
        if (from) from.style.viewTransitionName = PGAL_ZOOM_NAME;
        const transition = document.startViewTransition(() => {
          mutate();
          if (from) from.style.viewTransitionName = '';
          if (to) to.style.viewTransitionName = PGAL_ZOOM_NAME;
        });
        transition.finished.finally(() => {
          if (to) to.style.viewTransitionName = '';
        });
      }

      async open(index) {
        if (!this.modal) return;
        const from = this.tileImg(index);
        const to = this.stageImg(index);
        // The stage image is lazy and lives inside a hidden (display:none) modal,
        // so it may not have loaded — decode() would then hang forever. Force an
        // eager load and wait for decode, but bound it so opening never blocks.
        if (to) {
          to.loading = 'eager';
          if (to.decode) {
            await Promise.race([to.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 200))]);
          }
        }
        this.morph(from, to, () => {
          this.modal.hidden = false;
          document.body.style.overflow = 'hidden';
          document.addEventListener('keydown', this.onKeydown);
          this.show(index);
        });
      }
      close() {
        if (!this.modal) return;
        const from = this.stageImg(this.index);
        const to = this.tileImg(this.index);
        this.morph(from, to, () => {
          this.modal.hidden = true;
          document.body.style.overflow = '';
          document.removeEventListener('keydown', this.onKeydown);
          this.endZoom();
        });
      }
      step(dir) {
        const total = this.figures.length;
        this.show((this.index + dir + total) % total);
      }
      show(index) {
        this.index = index;
        this.figures.forEach((fig, i) => fig.classList.toggle('is-active', i === index));
        this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
        this.loadPane(index);
        // Default the preview + lens to the image centre (wait a frame so the
        // freshly-shown figure has laid out and getBoundingClientRect is valid).
        requestAnimationFrame(this.centerZoom);
      }
      // Progressive pane: show the already-loaded low-res stage image scaled up
      // (slightly soft) as an instant placeholder, then swap in the sharp
      // original once it downloads. `paneSrc` guards against a stale load winning
      // when the user switches images while one is still downloading.
      loadPane(index) {
        if (!this.pane) return;
        const src = this.figures[index]?.dataset.zoom;
        const placeholder = this.stageImg(index)?.src;
        this.paneSrc = src;
        this.pane.style.backgroundImage = placeholder ? `url("${placeholder}")` : '';
        if (!src || src === placeholder) return;
        const image = new Image();
        image.onload = () => {
          if (this.paneSrc !== src) return;
          this.pane.style.backgroundImage = `url("${src}")`;
        };
        image.src = src;
      }
      onKeydown(e) {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowLeft') this.step(-1);
        else if (e.key === 'ArrowRight') this.step(1);
      }

      /* Flipkart-style zoom: lens over the image (left), magnified view on the pane (right). */
      onZoom = (e) => {
        const img = this.activeImg;
        if (!img || !this.zoomQuery.matches) return;
        const rect = img.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Off the image → fall back to the centred default rather than hiding.
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) return this.centerZoom();
        this.applyZoom(rect, x, y);
      };
      // Default resting state: preview + lens locked to the image centre.
      centerZoom = () => {
        const img = this.activeImg;
        if (!img || !this.zoomQuery.matches) return;
        // Lazy stage images may not have loaded yet on first open, so their
        // box is 0×0 — centre once the image reports a size.
        if (!img.complete || !img.naturalWidth) {
          img.addEventListener('load', this.centerZoom, { once: true });
          return;
        }
        const rect = img.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        this.applyZoom(rect, rect.width / 2, rect.height / 2);
      };
      // Point (x,y) is relative to the image's top-left.
      applyZoom = (rect, x, y) => {
        if (!this.pane || !this.lens) return;
        // Higher Z = smaller lens (pane / Z) and a more zoomed-in right pane.
        const Z = 3.5;
        const lensW = this.pane.clientWidth / Z;
        const lensH = this.pane.clientHeight / Z;
        const lx = clamp(x - lensW / 2, 0, rect.width - lensW);
        const ly = clamp(y - lensH / 2, 0, rect.height - lensH);

        // Position lens relative to the stage (image is centred within it).
        const stageRect = this.stage.getBoundingClientRect();
        this.classList.add('is-zooming');
        this.lens.style.width = `${lensW}px`;
        this.lens.style.height = `${lensH}px`;
        this.lens.style.left = `${rect.left - stageRect.left + lx}px`;
        this.lens.style.top = `${rect.top - stageRect.top + ly}px`;

        this.pane.style.backgroundSize = `${rect.width * Z}px ${rect.height * Z}px`;
        this.pane.style.backgroundPosition = `-${lx * Z}px -${ly * Z}px`;
      };
      endZoom = () => {
        this.classList.remove('is-zooming');
      };
    }
  );
}

/* ------------------------------------------------------ Shop by Variant/Look */
if (!customElements.get('product-looks')) {
  customElements.define(
    'product-looks',
    class ProductLooks extends HTMLElement {
      static cache = new Map();

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.items = Array.from(this.querySelectorAll('[data-product-url]'));

        this.items.forEach((item) => {
          const url = item.dataset.productUrl;
          item.addEventListener('mouseenter', () => this.preload(url));
          item.addEventListener('focus', () => this.preload(url));
          item.addEventListener('click', (e) => this.onClick(e, item, url));
        });

        this.prev?.addEventListener('click', () => this.scrollBy(-1));
        this.next?.addEventListener('click', () => this.scrollBy(1));
        this.track?.addEventListener('scroll', this.updateArrows, { passive: true });
        this.updateArrows();
        window.addEventListener('resize', this.updateArrows);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.updateArrows);
      }

      scrollBy(dir) {
        if (!this.track) return;
        this.track.scrollBy({ left: dir * this.track.clientWidth * 0.8, behavior: 'smooth' });
      }
      updateArrows = () => {
        if (!this.track) return;
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        if (this.prev) this.prev.hidden = this.track.scrollLeft <= 1;
        if (this.next) this.next.hidden = this.track.scrollLeft >= max;
      };

      preload(url) {
        if (!url || ProductLooks.cache.has(url)) return;
        const promise = fetch(`${url}?section_id=${this.sectionId}`)
          .then((r) => r.text())
          .catch(() => null);
        ProductLooks.cache.set(url, promise);
      }

      async onClick(e, item, url) {
        if (!url || e.metaKey || e.ctrlKey || e.shiftKey) return; // let new-tab clicks through
        e.preventDefault();
        if (item.classList.contains('plooks__item--active')) return;

        item.classList.add('is-loading');
        this.preload(url);
        const html = await ProductLooks.cache.get(url);
        item.classList.remove('is-loading');
        if (!html) return void (window.location.href = url);

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('product-info');
        const current = this.closest('product-info');
        if (!next || !current) return void (window.location.href = url);

        const title = doc.querySelector('.pdp__title')?.textContent?.trim();
        current.replaceWith(next);
        // Server-rendered HTML: anything JS had adjusted in place (the shipping
        // bar's converted amounts) has to be reapplied to the new nodes.
        document.dispatchEvent(new CustomEvent('product-section:swap'));
        window.history.pushState({}, '', url);
        if (title) document.title = title;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  );
}

/* ------------------------------------------- Purchase area / size gate ----- */
if (!customElements.get('product-purchase')) {
  customElements.define(
    'product-purchase',
    class ProductPurchase extends HTMLElement {
      connectedCallback() {
        this.variantSelects = this.querySelector('variant-selects');
        this.sizeError = this.querySelector('[data-size-error]');
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.hasGate = !!this.variantSelects && !!this.sizeError;
        this.chosen = false;

        if (!this.hasGate) return;

        // Match the design: no size preselected until the shopper picks one.
        this.variantSelects.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));

        this.addEventListener('change', (e) => {
          if (e.target.matches('variant-selects input[type="radio"]')) {
            this.chosen = true;
            this.sizeError.hidden = true;
          }
        });
        // Capture on the ancestor so we run before product-form.js's submit handler.
        this.addEventListener('submit', this.onSubmit, true);
      }

      onSubmit = (e) => {
        if (!this.hasGate || this.chosen) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.sizeError.hidden = false;
        this.sizeError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      /* Called by <size-chart> when a size is chosen from the chart. */
      selectSize(value) {
        const input = this.variantSelects?.querySelector(
          `input[type="radio"][value="${window.CSS && CSS.escape ? CSS.escape(value) : value}"]`
        );
        if (input && !input.checked) input.click();
      }
    }
  );
}

/* ----------------------------------------------------------- Size chart ---- */
if (!customElements.get('size-chart')) {
  customElements.define(
    'size-chart',
    class SizeChart extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-szc-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-szc-panel]'));
        this.units = Array.from(this.querySelectorAll('[data-szc-unit]'));
        this.cells = Array.from(this.querySelectorAll('[data-in]'));

        this.querySelectorAll('[data-szc-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        this.tabs.forEach((t) => t.addEventListener('click', () => this.tab(t.dataset.szcTab)));
        this.units.forEach((u) => u.addEventListener('click', () => this.unit(u.dataset.szcUnit)));
        this.querySelectorAll('[data-size-value]').forEach((r) => r.addEventListener('change', () => this.pick(r.dataset.sizeValue)));
        /* Whole row is clickable — check its radio and select the size. */
        this.querySelectorAll('[data-szc-row]').forEach((row) =>
          row.addEventListener('click', () => {
            const input = row.querySelector('[data-size-value]');
            if (!input) return;
            input.checked = true;
            this.pick(input.dataset.sizeValue);
          })
        );
        this.onKeydown = (e) => e.key === 'Escape' && this.close();
      }

      open() {
        this.hidden = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        this.syncSelected();
      }
      close() {
        this.hidden = true;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeydown);
      }
      tab(name) {
        this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.szcTab === name));
        this.panels.forEach((p) => p.classList.toggle('is-active', p.dataset.szcPanel === name));
      }
      unit(u) {
        this.units.forEach((el) => el.classList.toggle('is-active', el.dataset.szcUnit === u));
        this.cells.forEach((c) => (c.textContent = u === 'cm' ? c.dataset.cm : c.dataset.in));
      }
      pick(value) {
        /* Update the PDP's selected size but keep the drawer open; it only closes
           via the backdrop or the close button. */
        this.sizeHost?.selectSize(value);
        this.markSelected(value);
      }
      /* Subtly highlight the chosen row (and keep its radio in sync). */
      markSelected(value) {
        this.querySelectorAll('[data-szc-row]').forEach((row) => {
          const input = row.querySelector('[data-size-value]');
          const on = !!input && input.dataset.sizeValue === value;
          row.classList.toggle('is-selected', on);
          if (input) input.checked = on;
        });
      }
      /* Reflect the PDP's current size when the drawer opens. */
      syncSelected() {
        const host = this.sizeHost;
        const checked = (host?.variantSelects ?? host)?.querySelector('input[type="radio"]:checked');
        if (checked?.value) this.markSelected(checked.value);
      }
      /* PDP: <product-purchase>; elsewhere (e.g. the PLP featured card): the nearest [data-size-host]. */
      get sizeHost() {
        return this.closest('product-info')?.querySelector('product-purchase') ?? this.closest('[data-size-host]');
      }
    }
  );
}

/* ------------------------------- Share + size-chart triggers (delegated) -- */
if (!window.__tssPdpDelegates) {
  window.__tssPdpDelegates = true;
  document.addEventListener('click', (e) => {
    const share = e.target.closest('[data-share]');
    if (share) {
      const url = share.dataset.shareUrl || location.href;
      const title = share.dataset.shareTitle || document.title;
      if (navigator.share) {
        navigator.share({ title, url }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
      return;
    }
    const trigger = e.target.closest('[data-size-chart]');
    if (trigger) {
      e.preventDefault();
      trigger.closest('product-info')?.querySelector('size-chart')?.open();
    }
  });
}

/* ----------------------------------------------------------------- Coupon -- */
// Delegated so it survives a <product-looks> section swap.
const COUPON_RESET_MS = 2000;

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-coupon-copy]');
  if (!button || button.hasAttribute('data-copied')) return;

  const code = button.closest('.pdp-coupon')?.querySelector('[data-coupon-code]')?.textContent?.trim();
  if (!code) return;

  try {
    await navigator.clipboard.writeText(code);
  } catch {
    return;
  }

  // The state is the icon: CSS swaps clipboard for tick off [data-copied].
  button.setAttribute('data-copied', '');
  setTimeout(() => button.removeAttribute('data-copied'), COUPON_RESET_MS);
});

/* ---------------------------------------------------------- Trust labels -- */
// A wrapped label's box stretches to the full space it's given, not to its
// longest line, so a centred icon + label sits visibly left of centre. CSS can't
// shrink-wrap wrapped text, so size each label to its widest line here.
const trustRowObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => fitTrustLabels(entry.target));
});

function fitTrustLabels(row) {
  row.querySelectorAll('.pdp__trust-label').forEach((label) => {
    label.style.width = '';
    const range = document.createRange();
    range.selectNodeContents(label);
    const lineWidths = Array.from(range.getClientRects(), (rect) => rect.width);
    // Hidden copies (desktop row on mobile and vice versa) have no line boxes.
    if (lineWidths.length) label.style.width = `${Math.ceil(Math.max(...lineWidths))}px`;
  });
}

function watchTrustRows() {
  document.querySelectorAll('.pdp__trust').forEach((row) => trustRowObserver.observe(row));
}

watchTrustRows();
document.addEventListener('product-section:swap', watchTrustRows);
// The web font changes every line width, and doesn't resize the row itself.
document.fonts?.ready.then(() => document.querySelectorAll('.pdp__trust').forEach(fitTrustLabels));
